import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import notificationsApi from '@/api/notificationsApi';
import type { NotificationModel } from '@/api/types';

// The Notify endpoints return bare payloads (no { data } envelope), so the
// query data IS the array/object directly. Action endpoints return an opaque
// result summary (unknown).
type NotificationPayload = Record<string, unknown>;
type NotificationListResponse = NotificationModel[];
type NotificationResponse = NotificationModel;
type NotificationActionResult = unknown;

// Query keys for notification-related queries
export const NOTIFICATIONS_KEYS = {
  all: ['notifications'] as const,
  unread: ['notifications', 'unread'] as const,
  byId: (id: string) => ['notifications', id] as const,
};

/**
 * Hook for fetching all notifications
 */
export function useNotifications(
  options: ApiQueryOptions<NotificationListResponse> = {},
) {
  return useAuthenticatedQuery<NotificationListResponse>(
    NOTIFICATIONS_KEYS.all,
    token => notificationsApi.getNotifications(token),
    options,
  );
}

/**
 * Hook for fetching unread notifications
 */
export function useUnreadNotifications(
  options: ApiQueryOptions<NotificationListResponse> = {},
) {
  return useAuthenticatedQuery<NotificationListResponse>(
    NOTIFICATIONS_KEYS.unread,
    token => notificationsApi.getNotifications(token),
    options,
  );
}

/**
 * Hook for fetching a specific notification by ID
 */
export function useNotification(
  id: string,
  options: ApiQueryOptions<NotificationResponse> = {},
) {
  return useAuthenticatedQuery<NotificationResponse>(
    NOTIFICATIONS_KEYS.byId(id),
    token => notificationsApi.getNotificationById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for creating a notification
 */
export function useCreateNotification(
  options: ApiMutationOptions<
    NotificationResponse,
    { data: NotificationPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    NotificationResponse,
    { data: NotificationPayload }
  >(({ data, token }) => notificationsApi.createNotification(data, token), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.unread });
    },
    ...options,
  });
}

/**
 * Hook for marking notification as read
 */
export function useMarkNotificationRead(
  options: ApiMutationOptions<NotificationResponse, { id: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<NotificationResponse, { id: string }>(
    ({ id, token }) => notificationsApi.markAsRead(id, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: NOTIFICATIONS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.unread });
      },
      ...options,
    },
  );
}

/**
 * Hook for marking all notifications as read
 */
export function useMarkAllNotificationsRead(
  options: ApiMutationOptions<
    NotificationActionResult,
    { token?: string }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<NotificationActionResult, { token?: string }>(
    ({ token }) => notificationsApi.markAllAsRead(token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.unread });
      },
      ...options,
    },
  );
}

/**
 * Hook for deleting a notification
 */
export function useDeleteNotification(
  options: ApiMutationOptions<NotificationActionResult, { id: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<NotificationActionResult, { id: string }>(
    ({ id, token }) => notificationsApi.deleteNotification(id, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.removeQueries({
          queryKey: NOTIFICATIONS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.unread });
      },
      ...options,
    },
  );
}

/**
 * Hook for disabling all notifications (clear all)
 */
export function useDisableAllNotifications(
  options: ApiMutationOptions<
    NotificationActionResult,
    { token?: string }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<NotificationActionResult, { token?: string }>(
    ({ token }) => notificationsApi.disableAllNotifications(token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.unread });
      },
      ...options,
    },
  );
}
